const ORIGIN = (
  process.env.REACT_APP_LEGAL_PORTAL_ORIGIN || "https://legal.scribeinai.com"
).replace(/\/+$/, "");

export const SITE = {
  origin: ORIGIN,
  name: "ScribeInAI Legal Dashboard",
  shortName: "Legal Portal",
  brand: "ScribeInAI",
  locale: "en_US",
  language: "en",
  themeColor: "#4A2C1A",
  imagePath: "/legal-login-hero.png",
  logoPath: "/image.png",
  imageAlt: "ScribeInAI legal transcription dashboard — dictate, track, and download transcripts",
  sameAs: ["https://scribeinai.com"],
  keywords: [
    "legal transcription dashboard",
    "legal dictation software",
    "AI legal transcription",
    "attorney transcription portal",
    "law firm dictation",
    "legal transcript tracking",
    "ScribeInAI legal",
    "court reporting workflow",
    "legal document transcription",
  ].join(", "),
};

export const DEFAULT_DESCRIPTION =
  "ScribeInAI Legal Dashboard — secure AI legal transcription for law firms. Dictate, upload audio, track jobs, manage templates, and download finished transcripts.";

export const PAGES = {
  home: {
    title: "Legal Transcription Dashboard | ScribeInAI",
    description: DEFAULT_DESCRIPTION,
    canonicalPath: "/",
    path: "/",
  },
  login: {
    title: "Sign In · Legal Transcription Dashboard | ScribeInAI",
    description:
      "Sign in to the ScribeInAI Legal Dashboard to dictate, track transcription jobs, and download finished legal transcripts for your firm.",
    canonicalPath: "/",
    path: "/login",
  },
  register: {
    title: "Create Legal Account · Legal Dashboard | ScribeInAI",
    description:
      "Register for the ScribeInAI Legal Dashboard. Create a legal team profile so an administrator can verify access to AI legal transcription.",
    canonicalPath: "/register",
    path: "/register",
  },
  dashboard: {
    title: "Legal Dashboard · Analytics, Dictate & Transcripts | ScribeInAI",
    description:
      "Open your ScribeInAI Legal Dashboard: analytics, dictation, pending files, completed transcripts, and template management for legal teams.",
    canonicalPath: "/legal-dashboard",
    path: "/legal-dashboard",
  },
  profile: {
    title: "Profile · Legal Dashboard | ScribeInAI",
    description: "Manage your ScribeInAI Legal Dashboard profile and account settings.",
    canonicalPath: "/profile",
    path: "/profile",
    noindex: true,
  },
};

export function absoluteUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  return p === "/" ? `${SITE.origin}/` : `${SITE.origin}${p}`;
}

export function ogImageUrl() {
  return absoluteUrl(SITE.imagePath);
}

export function logoUrl() {
  return absoluteUrl(SITE.logoPath);
}

export function buildGraphLd({ title, description, canonical, pagePath }) {
  const orgId = `${SITE.origin}/#organization`;
  const websiteId = `${SITE.origin}/#website`;
  const appId = `${SITE.origin}/#app`;
  const webpageId = `${canonical}#webpage`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: SITE.brand,
        url: "https://scribeinai.com",
        logo: {
          "@type": "ImageObject",
          url: logoUrl(),
        },
        sameAs: SITE.sameAs,
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        name: SITE.name,
        url: `${SITE.origin}/`,
        inLanguage: SITE.language,
        publisher: { "@id": orgId },
      },
      {
        "@type": "SoftwareApplication",
        "@id": appId,
        name: SITE.name,
        applicationCategory: "BusinessApplication",
        applicationSubCategory: "Legal transcription",
        operatingSystem: "Web",
        url: `${SITE.origin}/`,
        image: ogImageUrl(),
        description: DEFAULT_DESCRIPTION,
        featureList: [
          "Legal dictation",
          "AI legal transcription",
          "Job analytics",
          "Pending file tracking",
          "Completed transcript download",
          "Template and sample management",
        ],
        publisher: { "@id": orgId },
      },
      {
        "@type": "WebPage",
        "@id": webpageId,
        url: canonical,
        name: title,
        description,
        inLanguage: SITE.language,
        isPartOf: { "@id": websiteId },
        about: { "@id": appId },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: ogImageUrl(),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: SITE.brand,
            item: "https://scribeinai.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: SITE.shortName,
            item: `${SITE.origin}/`,
          },
          ...(pagePath && pagePath !== "/"
            ? [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: title,
                  item: canonical,
                },
              ]
            : []),
        ],
      },
    ],
  };
}
